import {computed, DestroyRef, effect, inject, Injectable, signal} from '@angular/core';
import {FormControl} from '@angular/forms';
import {TodoCompletion} from '../../../../api';
import {TodoCompletionService} from '../../todos/services/todo-completion.service';

// Narrowed to what the service actually needs, so any todo projection can feed it.
type CompletableTodo = { id: number; completion?: TodoCompletion };

type PendingCompletion = { kind: 'todo'; item: CompletableTodo };

@Injectable()
export class PendingCompletionService {

  private readonly completionService = inject(TodoCompletionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly DELAY_SECONDS = 5;
  private pendingTimer: ReturnType<typeof setTimeout> | null = null;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private readonly completionControls = new Map<number, FormControl<boolean>>();

  pendingCompletions = signal<PendingCompletion[]>([]);
  hasPending = computed(() => this.pendingCompletions().length > 0);
  countdown = signal<number>(this.DELAY_SECONDS);

  constructor() {
    this.destroyRef.onDestroy(() => this.cancelCompletion());
  }

  syncControls(todos: CompletableTodo[]) {
    const allIds = new Set(todos.map(todo => todo.id));
    for (const id of this.completionControls.keys()) {
      if (!allIds.has(id)) this.completionControls.delete(id);
    }
  }

  getCompletionControl(todo: CompletableTodo): FormControl<boolean> {
    let ctrl = this.completionControls.get(todo.id);
    if (!ctrl) {
      ctrl = new FormControl<boolean>(todo.completion === TodoCompletion.DONE, {nonNullable: true});
      this.completionControls.set(todo.id, ctrl);
    }
    return ctrl;
  }

  toggleCompletion(event: any, todo: CompletableTodo) {
    event.stopPropagation();
    if (todo.completion === TodoCompletion.DONE) return;
    const isAlreadyPending = this.pendingCompletions().some(p => p.item.id === todo.id);
    if (isAlreadyPending) {
      this.pendingCompletions.update(list => list.filter(p => p.item.id !== todo.id));
    } else {
      this.pendingCompletions.update(list => [...list, {kind: 'todo', item: todo}]);
    }
    this.resetTimer();
  }

  cancelCompletion() {
    this.pendingCompletions().forEach(p => {
      this.completionControls.get(p.item.id)?.setValue(false, {emitEvent: false});
    });
    this.pendingCompletions.set([]);
    this.clearTimers();
  }

  private resetTimer() {
    this.clearTimers();
    if (!this.pendingCompletions().length) return;
    this.countdown.set(this.DELAY_SECONDS);
    this.countdownInterval = setInterval(() => this.countdown.update(v => v - 1), 1000);
    this.pendingTimer = setTimeout(() => this.commitCompletion(), this.DELAY_SECONDS * 1000);
  }

  private clearTimers() {
    if (this.pendingTimer) {
      clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private async commitCompletion() {
    const pending = this.pendingCompletions();
    this.pendingCompletions.set([]);
    this.clearTimers();
    if (!pending.length) return;
    await Promise.all(pending.map(p =>
      this.completionService.changeTodoCompletion(p.item.id, TodoCompletion.DONE)
    ));
    this.completionService.invalidateAll();
  }
}
